import { Container, type Text } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import { SpriteText, SpriteTextFitMode } from "../../sprite-text/SpriteText";
import type { WinAmountLabelCollectTuning } from "./WinAmountLabelCollectTuning";
import type { WinAmountLabelTuningBase } from "./WinAmountLabelTuningBase";

/**
 * Per-line "base x mult → final" label breakdown. `baseText` is already money-formatted;
 * `multiplier` is the raw multiplier (e.g. "3").
 */
export interface LabelBreakdown {
  baseText: string;
  multiplier: string;
}

/**
 * Shared base for the win-amount-label variants. Holds the common wiring — the `node` (the label
 * root the layer positions + destroys), the `spriteText` / `label` renderers, the injected engine
 * clock, the show/hide generation token, and the two exits: the alpha-fade `hide()` and the
 * `collectTo()` flight into a win meter. Each VARIANT subclasses this and overrides `show()` with
 * its own entrance.
 *
 * The GAME builds the label's display tree (it is game art: glow, font, Spine) through a factory
 * the layer calls per label; this class is what the layer talks to.
 *
 * Animations run on the injected `PresentationTaskRunner`, so they pause / frame-step / speed-scale
 * with the rest of the win presentation. The generation token cancels an in-flight `show()` the
 * moment a new `show()` / `hide()` starts.
 */
export class WinAmountLabel {
  /** The label root — positioned by the layer, hidden/scaled by the label itself. */
  public readonly node: Container;
  public readonly spriteText: SpriteText | null;
  public readonly label: Text | null;

  protected tuning: WinAmountLabelTuningBase | null = null;
  protected taskRunner: PresentationTaskRunner | null = null;
  /** Bumped per show()/hide(); a stale in-flight animation stops writing the node. */
  protected generation = 0;
  /** The digits once `collectTo` lifted them out of `node`: they fly outside it, and `hide()` / `destroy()` take them too. */
  private flyingDigits: Container | null = null;

  public constructor(input: { node?: Container; spriteText?: SpriteText | null; label?: Text | null }) {
    this.node = input.node ?? new Container({ label: "WinAmountLabel" });
    this.spriteText = input.spriteText ?? null;
    this.label = input.label ?? null;
    this.node.alpha = 0;
    this.node.scale.set(0, 0);
  }

  /**
   * True when the amount draws with the sprite font, which has no currency glyph: the labels format
   * it without the symbol (plan §2.4). A variant whose font carries the glyph overrides this.
   */
  public get amountOmitsCurrencySymbol(): boolean {
    return this.spriteText !== null;
  }

  public setTuning(tuning: WinAmountLabelTuningBase): void {
    this.tuning = tuning;
  }

  public setTaskRunner(taskRunner: PresentationTaskRunner): void {
    this.taskRunner = taskRunner;
  }

  /** Apply the text to whichever renderer is wired (sprite-text preferred, else the Pixi Text). */
  protected applyText(text: string): void {
    if (this.spriteText) {
      this.spriteText.setText(text);
    } else if (this.label) {
      this.label.text = text;
    }
  }

  /** Variant entry point — OVERRIDDEN per variant. */
  public show(_text: string, _breakdown?: LabelBreakdown): void {
    /* overridden by the variant subclass */
  }

  /** Morph an ALREADY-SHOWN base label to "base x mult → final" (deferred merge). Base snaps. */
  public mergeToFinal(finalText: string, _breakdown: LabelBreakdown): Promise<void> {
    this.applyText(finalText);
    return Promise.resolve();
  }

  /** Pop-replace an ALREADY-SHOWN amount STRAIGHT to `finalText` (substep-total merge). Base snaps. */
  public popReplaceTo(finalText: string): Promise<void> {
    this.applyText(finalText);
    return Promise.resolve();
  }

  /** Update an already-shown amount with a small emphasis bump (substep accumulation). Base snaps. */
  public bumpAmount(text: string): Promise<void> {
    this.applyText(text);
    return Promise.resolve();
  }

  /**
   * The collect exit, shared by all variants: the digits leave the label for `target` (a win meter's amount) — they
   * swell, then fly on a slight arc that shrinks them to the target's digit height — while the rest of the label (its
   * plate, its Spine) fades where it stands. `onContact` fires once, as the digits touch the target (their centres one
   * of its digit heights apart; the target counts then), and they sink into it, fading with the distance left; the
   * promise resolves as they land, the label spent. Every wait runs under `scopeId`, so a slam of it lands the digits
   * at once. Bumps the generation.
   */
  public async collectTo(target: Container, tuning: WinAmountLabelCollectTuning, scopeId: string | undefined, onContact: () => void): Promise<void> {
    const runner = this.taskRunner;
    const digits = this.spriteText ?? this.label;
    const flightLayer = this.node.parent;
    if (!runner || !digits || digits.destroyed || !flightLayer) {
      onContact();
      await this.hide();
      return;
    }
    const gen = ++this.generation;
    const isCurrent = (): boolean => this.generation === gen && !digits.destroyed;
    let touched = false;
    const contact = (): void => {
      if (touched) return;
      touched = true;
      onContact();
    };

    // The digits leave the label (a Spine variant's bone socket) for the label's own layer, where they stand on screen.
    const restHeight = digits.getBounds().height;
    const landingHeight = digitHeightOf(target);
    const layerWorld = flightLayer.getGlobalTransform();
    const onScreen = layerWorld.clone().invert().append(digits.getGlobalTransform());
    flightLayer.addChild(digits);
    digits.setFromMatrix(onScreen);
    this.flyingDigits = digits;

    const node = this.node;
    const plateFrom = node.alpha;
    void runner.tweenProgress(
      this.tuning?.hideSeconds ?? 0,
      (k) => {
        if (this.generation === gen && !node.destroyed) node.alpha = plateFrom * (1 - k);
      },
      scopeId,
      { easing: "linear" },
    );

    const rest = { x: digits.scale.x, y: digits.scale.y };
    if (tuning.popSeconds > 0 && tuning.popScale !== 1) {
      await runner.tweenProgress(
        tuning.popSeconds,
        (k) => {
          const swell = 1 + (tuning.popScale - 1) * k;
          if (isCurrent()) digits.scale.set(rest.x * swell, rest.y * swell);
        },
        scopeId,
        { easing: "quadOut" },
      );
    }

    // An arc to the target's origin, down to its digit height (without one the digits keep their size). They touch it
    // one of its digit heights out, in the layer's units.
    const shrink = landingHeight !== null && restHeight > 0 ? landingHeight / restHeight : 1;
    const touch = (landingHeight ?? restHeight) / (Math.hypot(layerWorld.c, layerWorld.d) || 1);
    const from = { x: digits.position.x, y: digits.position.y, scaleX: digits.scale.x, scaleY: digits.scale.y, rotation: digits.rotation, alpha: digits.alpha };
    const to = flightLayer.toLocal(target.getGlobalPosition());
    const control = arcControl(from, to, tuning.arcHeight);
    await runner.tweenProgress(
      tuning.flySeconds,
      (k) => {
        if (!isCurrent()) return;
        const u = 1 - k;
        const x = u * u * from.x + 2 * u * k * control.x + k * k * to.x;
        const y = u * u * from.y + 2 * u * k * control.y + k * k * to.y;
        digits.position.set(x, y);
        digits.scale.set(from.scaleX + (rest.x * shrink - from.scaleX) * k, from.scaleY + (rest.y * shrink - from.scaleY) * k);
        digits.rotation = from.rotation * u;
        const left = Math.hypot(to.x - x, to.y - y);
        if (left <= touch) {
          contact();
          digits.alpha = touch > 0 ? from.alpha * (left / touch) : 0;
        }
      },
      scopeId,
      { easing: tuning.flyEasing },
    );
    contact();
    if (isCurrent()) digits.visible = false;
  }

  /** Alpha fade-out — shared by all variants; digits in flight fade with it. Bumps the generation (cancels the show() timelines and a collect). */
  public hide(): Promise<void> {
    const node = this.node;
    const digits = this.flyingDigits;
    const t = this.tuning;
    const runner = this.taskRunner;
    if (!t || !runner) {
      node.alpha = 0;
      if (digits) digits.alpha = 0;
      return Promise.resolve();
    }

    const gen = ++this.generation;
    const isCurrent = (): boolean => this.generation === gen && !node.destroyed;
    const from = node.alpha;
    const digitsFrom = digits?.alpha ?? 0;
    return runner.tweenProgress(
      t.hideSeconds,
      (k) => {
        if (!isCurrent()) return;
        node.alpha = from * (1 - k);
        if (digits && !digits.destroyed) digits.alpha = digitsFrom * (1 - k);
      },
      undefined,
      { easing: "linear" },
    );
  }

  public get destroyed(): boolean {
    return this.node.destroyed;
  }

  public destroy(): void {
    this.generation++;
    if (!this.node.destroyed) this.node.destroy({ children: true });
    if (this.flyingDigits && !this.flyingDigits.destroyed) this.flyingDigits.destroy({ children: true });
  }
}

/**
 * The control point of a quadratic arc from `from` to `to` whose middle bows `height` off the straight line: on the
 * path's upper side (y grows down), to the right of a vertical one; a negative height bows the other way.
 */
function arcControl(from: { x: number; y: number }, to: { x: number; y: number }, height: number): { x: number; y: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  let nx = -dy / length;
  let ny = dx / length;
  if (ny > 0 || (ny === 0 && nx < 0)) {
    nx = -nx;
    ny = -ny;
  }
  // A quadratic curve's middle lies halfway between the chord's middle and the control point.
  return { x: (from.x + to.x) / 2 + nx * height * 2, y: (from.y + to.y) / 2 + ny * height * 2 };
}

/**
 * The height, in world units, collected digits shrink to: the target's drawn amount, or — while it draws nothing, as
 * a meter cleared for a new spin does — the box a fitted sprite text draws its digits to. Null: they keep their size.
 */
function digitHeightOf(target: Container): number | null {
  const drawn = target.getBounds().height;
  if (drawn > 0) return drawn;
  const fitsHeight = target instanceof SpriteText && (target.fitMode === SpriteTextFitMode.Height || target.fitMode === SpriteTextFitMode.Both);
  if (!fitsHeight || target.boxHeight <= 0) return null;
  const world = target.getGlobalTransform();
  return target.boxHeight * Math.hypot(world.c, world.d);
}
